import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { SearchService } from '../../services/search.service';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [TranslateModule, CommonModule, FormsModule],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit {
  // Parametri di ricerca
  searchParams = {
    query: '',
    category: 'products',
    startDate: '',
    endDate: '',
    status: '',
    page: 1,
    pageSize: 10
  };

  // Stato della ricerca
  isLoading = false;
  searchPerformed = false;
  searchResults: any[] = [];
  currentPage = 1;
  totalPages = 1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private searchService: SearchService,
    private authService: AuthService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Recupera i parametri dalla URL
    this.route.queryParams.subscribe(params => {
      if (params['query'] || params['category'] || params['startDate'] || params['endDate'] || params['status']) {
        this.searchParams.query = params['query'] || '';
        this.searchParams.category = params['category'] || 'products';
        this.searchParams.startDate = params['startDate'] || '';
        this.searchParams.endDate = params['endDate'] || '';
        this.searchParams.status = params['status'] || '';
        this.searchParams.page = parseInt(params['page']) || 1;

        // Esegui la ricerca automaticamente se ci sono parametri nell'URL
        this.performSearch();
      }
    });

    // Recupera eventuali filtri salvati in localStorage
    const savedCategory = localStorage.getItem('selectedCategory');
    if (savedCategory && !this.searchParams.category) {
      this.searchParams.category = savedCategory;
    }
  }

  performSearch(): void {
    this.isLoading = true;
    this.searchPerformed = true;

    // Aggiorna la URL con i parametri di ricerca correnti
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.searchParams.query || null,
        category: this.searchParams.category,
        startDate: this.searchParams.startDate || null,
        endDate: this.searchParams.endDate || null,
        status: this.searchParams.status || null,
        page: this.searchParams.page
      },
      queryParamsHandling: 'merge'
    });

    // Chiamata al servizio di ricerca
    this.searchService.search(this.searchParams)
      .subscribe(
        (response: any) => {
          this.searchResults = response.results;
          this.totalPages = Math.ceil(response.total / this.searchParams.pageSize);
          this.currentPage = this.searchParams.page;
          this.isLoading = false;
        },
        (error: any) => {
          console.error('Errore durante la ricerca:', error);
          this.isLoading = false;
          this.searchResults = [];

          // Se c'è un errore di autenticazione, reindirizza alla pagina di login
          if (error.status === 401) {
            this.authService.logout();
            this.router.navigate(['/login']);
          }
        }
      );
  }

  resetSearch(): void {
    this.searchParams = {
      query: '',
      category: 'products',
      startDate: '',
      endDate: '',
      status: '',
      page: 1,
      pageSize: 10
    };
    this.searchPerformed = false;
    this.searchResults = [];

    // Pulisci i parametri nell'URL
    this.router.navigate(['/search']);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;

    this.searchParams.page = page;
    this.performSearch();
  }

  viewDetail(result: any): void {
    // Naviga alla pagina di dettaglio in base alla categoria
    this.router.navigate([`/details/${this.searchParams.category}/${result.id}`]);
  }

  downloadPdf(result: any): void {
    // Implementazione del download del PDF
    this.searchService.downloadPdf(this.searchParams.category, result.id)
      .subscribe(
        (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${result.code}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        },
        (error: any) => {
          console.error('Errore durante il download del PDF:', error);
        }
      );
  }

  getColumnTitle(category: string, column: string): string {
    // Restituisce il titolo della colonna in base alla categoria selezionata
    const columnMap: Record<string, Record<string, string>> = {
      products: {
        code: this.translate.instant('SEARCH.COLUMN.PRODUCT_CODE'),
        description: this.translate.instant('SEARCH.COLUMN.PRODUCT_DESCRIPTION'),
        date: this.translate.instant('SEARCH.COLUMN.CREATED_DATE'),
        status: this.translate.instant('SEARCH.COLUMN.STATUS')
      },
      waybills: {
        code: this.translate.instant('SEARCH.COLUMN.WAYBILL_NUMBER'),
        description: this.translate.instant('SEARCH.COLUMN.CUSTOMER'),
        date: this.translate.instant('SEARCH.COLUMN.SHIPMENT_DATE'),
        status: this.translate.instant('SEARCH.COLUMN.STATUS')
      },
      // Altre categorie...
    };

    return columnMap[category]?.[column] || column;
  }

  getStatusLabel(status: string): string {
    // Restituisce l'etichetta dello stato tradotta
    const statusMap: Record<string, string> = {
      completed: this.translate.instant('SEARCH.STATUS_COMPLETED'),
      in_progress: this.translate.instant('SEARCH.STATUS_IN_PROGRESS'),
      pending: this.translate.instant('SEARCH.STATUS_PENDING')
    };

    return statusMap[status] || status;
  }
}
